import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Brain, Database, Cloud, LineChart, CheckCircle2, TrendingUp, Clock, Factory } from 'lucide-react';
import SEOHead from '../../components/SEOHead';

const technologies = [
  { name: 'Azure Machine Learning', icon: Brain },
  { name: 'Databricks', icon: Database },
  { name: 'Power BI', icon: LineChart },
  { name: 'SAP Integration', icon: Cloud },
  { name: 'Kafka Streaming', icon: Database }
];

const keyMetrics = [
  {
    icon: TrendingUp,
    value: '88%',
    label: 'Prediction Accuracy',
    description: 'In targeted demand scenarios through advanced ML algorithms'
  },
  {
    icon: Factory,
    value: '40%',
    label: 'Safety Stock Reduction',
    description: 'While maintaining service levels'
  },
  {
    icon: Clock,
    value: 'Hours',
    label: 'Forecast Preparation',
    description: 'Reduced from days to hours'
  },
  {
    icon: CheckCircle2,
    value: '35%',
    label: 'Inventory Cost Reduction',
    description: 'Through optimized stock levels'
  }
];

const highlights = [
  {
    category: 'Forecasting Excellence',
    items: [
      'Achieved 88% prediction accuracy in targeted demand scenarios through advanced ML algorithms',
      'Virtually eliminated stock-out incidents through proactive inventory management',
      'Reduced forecast preparation time from days to hours'
    ]
  },
  {
    category: 'Operational Efficiency',
    items: [
      'Reduced safety stock levels by 40% while maintaining service levels',
      'Cut inventory carrying costs by 35% through optimized stock levels',
      'Delivered real-time supply chain visibility across all product lines'
    ]
  }
];

const challenges = [
  {
    title: 'Fragmented Enterprise Data Landscape',
    icon: Database,
    problem: 'Critical demand data was scattered across SAP ERP, Siemens MES, and Oracle SCM systems, with additional Excel-based projections creating inconsistent demand signals and making comprehensive forecasting nearly impossible.',
    solution: 'Our data engineering specialists implemented a unified data integration platform using Azure Data Factory and Kafka streaming to automatically ingest REST/API feeds from all enterprise systems. We created a centralized Databricks Delta Lake that stores structured and time-series data with ACID transactions and time-travel versioning capabilities.',
    impact: 'Unified data from all enterprise systems'
  },
  {
    title: 'Inaccurate Manual Forecasting',
    icon: Brain,
    problem: 'Production planners relied on manual Excel-based forecasting methods that failed to capture complex demand patterns, seasonal variations, and external market factors, leading to persistent forecast errors and suboptimal inventory decisions.',
    solution: 'We developed an advanced machine learning forecasting engine using extensive feature engineering to enrich model inputs with historical sales data, seasonal patterns, market trends, and external economic indicators. The system achieved 88% prediction accuracy through ensemble algorithms and continuous model refinement.',
    impact: '88% improvement in forecast accuracy'
  },
  {
    title: 'Reactive Inventory Management',
    icon: LineChart,
    problem: 'Manufacturing teams operated reactively to demand changes, often discovering stock-outs or excess inventory after problems had already impacted customer service levels and carrying costs, creating operational inefficiencies and lost revenue.',
    solution: 'Using Power BI, we built real-time dashboards that provide comprehensive forecast insights with automated email notifications for potential stock discrepancies and forecast anomalies. The system enables proactive inventory management with early warning systems and automated replenishment recommendations.',
    impact: 'Proactive inventory management implemented'
  },
  {
    title: 'Disconnected Production Planning',
    icon: Cloud,
    problem: 'Production schedules were often misaligned with actual demand patterns due to delayed information flow between forecasting, inventory management, and manufacturing execution systems, resulting in frequent order delays and stock imbalances.',
    solution: 'We implemented real-time data streaming using Kafka to ensure immediate information flow between all systems. The integrated platform provides production planners with up-to-date demand forecasts, inventory levels, and production capacity information for optimal scheduling decisions.',
    impact: 'Real-time production planning alignment'
  }
];

const ManufacturingAI = () => {
  return (
    <>
      <SEOHead
        title="Manufacturing AI Case Study | Agility"
        description="AI-powered predictive maintenance and quality control for manufacturing."
        url="https://www.agilitytech.ai/case-studies/manufacturing-ai"
      />
    <div className="pt-20 bg-black">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-r from-blue-900 to-black overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80"
            alt="Manufacturing AI Banner"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center px-4 py-2 bg-teal-500/10 rounded-full border border-teal-500/20 mb-6">
              <span className="text-teal-400 text-sm font-medium">Manufacturing/Supply Chain</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              AI-Driven Demand Forecasting Revolution
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed max-w-3xl">
              Transforming manufacturing operations through intelligent forecasting and real-time 
              analytics to eliminate stock-outs and optimize inventory management across the supply chain.
            </p>
          </div>
        </div>
      </section>

      {/* Key Metrics Section */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {keyMetrics.map((metric, index) => (
              <div key={index} className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 
                border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300 text-center">
                <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <metric.icon className="h-6 w-6 text-teal-400" />
                </div>
                <div className="text-3xl font-bold text-white mb-2">{metric.value}</div>
                <div className="text-teal-400 font-medium mb-2">{metric.label}</div>
                <p className="text-gray-400 text-sm">{metric.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Details */}
      <section className="py-12 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Project Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-teal-400 mb-2">Industry</h3>
              <p className="text-white">Manufacturing/Supply Chain</p>
            </div>
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-teal-400 mb-2">Region</h3>
              <p className="text-white">North America</p>
            </div>
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-teal-400 mb-2">Project Size</h3>
              <p className="text-white">Multi-Site Manufacturing</p>
            </div>
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-teal-400 mb-2">Time Frame</h3>
              <p className="text-white">Q4 2023 - Q1 2024</p>
            </div>
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-12 bg-gray-900 border-t border-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-xl font-bold text-white mb-6 text-center">Technology Stack</h3>
          <div className="flex flex-wrap justify-center gap-6">
            {technologies.map((tech, index) => (
              <div key={index} className="flex items-center space-x-3 bg-gray-800/30 rounded-lg px-4 py-3 border border-gray-700/30">
                <tech.icon className="h-6 w-6 text-teal-400" />
                <span className="text-gray-300 font-medium">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8">The Challenge</h2>
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-gray-800/50">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-500/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                  <Factory className="h-6 w-6 text-red-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-4">Fragmented Forecasting Crisis</h3>
                  <p className="text-xl text-gray-300 leading-relaxed">
                    A mid-sized manufacturing company operating across multiple product lines struggled 
                    with fragmented data from SAP ERP, Siemens MES, and Oracle SCM systems, combined 
                    with Excel-based demand projections that led to inconsistent demand signals. Manual 
                    forecasting processes caused excess inventory accumulation and frequent stock-outs, 
                    inflating carrying costs and resulting in lost revenue opportunities.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-20 bg-black border-t border-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Transformational Results</h2>
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-gray-800/50 mb-12">
              <div className="flex items-start space-x-4 mb-8">
                <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                  <CheckCircle2 className="h-6 w-6 text-green-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-4">Intelligent Forecasting Revolution</h3>
                  <p className="text-xl text-gray-300 leading-relaxed">
                    We replaced manual, error-prone forecasting processes with an intelligent, automated 
                    system that transformed inventory management across the entire manufacturing operation. 
                    By implementing cutting-edge machine learning algorithms with real-time data integration, 
                    we delivered breakthrough operational outcomes that exceeded all stakeholder expectations.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {highlights.map((category, index) => (
                <div key={index} className="bg-gray-900/30 backdrop-blur-sm rounded-xl p-6 border border-gray-800/30">
                  <h4 className="text-lg font-bold text-teal-400 mb-4">{category.category}</h4>
                  <div className="space-y-4">
                    {category.items.map((item, idx) => (
                      <div key={idx} className="flex items-start space-x-3">
                        <CheckCircle2 className="flex-shrink-0 h-5 w-5 text-green-400 mt-1" />
                        <p className="text-gray-300 text-sm leading-relaxed">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Challenges & Solutions */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-12 text-center">Challenges & Solutions</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {challenges.map((challenge, index) => (
              <div key={index} className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-8 
                border border-gray-700/50 hover:bg-gray-800/70 transition-all duration-300">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center">
                    <challenge.icon className="h-6 w-6 text-teal-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white">{challenge.title}</h3>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-semibold text-red-400 mb-3 flex items-center">
                      <span className="w-2 h-2 bg-red-400 rounded-full mr-3"></span>
                      Problem
                    </h4>
                    <p className="text-gray-300 leading-relaxed pl-5">{challenge.problem}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-semibold text-green-400 mb-3 flex items-center">
                      <span className="w-2 h-2 bg-green-400 rounded-full mr-3"></span>
                      Solution
                    </h4>
                    <p className="text-gray-300 leading-relaxed pl-5">{challenge.solution}</p>
                  </div>
                  
                  <div className="bg-teal-500/10 rounded-lg p-4 border border-teal-500/20">
                    <h4 className="text-sm font-semibold text-teal-400 mb-2">Impact</h4>
                    <p className="text-teal-300 font-medium">{challenge.impact}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="w-16 h-16 bg-teal-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Factory className="h-8 w-8 text-teal-400" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-6">
              Ready to Transform Your Forecasting Capabilities?
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Contact our analytics specialists to discover how intelligent forecasting can optimize 
              your manufacturing operations.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center space-x-3 bg-teal-500 text-white px-8 py-4 
                rounded-lg text-lg font-medium hover:bg-teal-400 transition-all duration-300 group
                shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              <span>Get Started Today</span>
              <ArrowRight className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default ManufacturingAI;